import React, { useMemo } from "react";
import {
	Box,
	Button,
	Alert,
	IconButton,
	Tooltip,
} from "@mui/material";
import AddCircle from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import Datagrid from "../Datagrid/Datagrid";
import { useAuth } from "../../context/authContext";

const CategoryViewModal = ({
	open,
	category,
	documents = [],
	columns = [],
	onClose,
	onViewDocument,
	onRemoveDocument,
	onAddDocument,
	onAssignDocuments,
	onDeleteCategory,
}) => {
	const { t } = useTranslation();
	const { hasPermission } = useAuth();
    const canCreateDocuments = hasPermission("document_management.create_documents");
    const canEditDocuments = hasPermission("document_management.edit_documents");
    const canDeleteDocuments = hasPermission("document_management.delete_documents");
    const canAssignDocuments = canCreateDocuments || canEditDocuments;
    const isDefaultCategory = Number(category?.id) === 1 || category?.is_default;

	const fallbackColumns = useMemo(
		() => [
			{
				field: "name",
				headerName: t("TABLE_HEADERS.DOCUMENT_NAME") || "Document",
				flex: 1,
			},
			{
				field: "description",
				headerName: t("TABLE_HEADERS.DESCRIPTION") || "Description",
				flex: 1.5,
			},
		],
		[t]
	);

	const datagridColumns = useMemo(() => {
		const baseColumns = Array.isArray(columns) && columns.length > 0 ? columns : fallbackColumns;
		if (!onRemoveDocument) return baseColumns;
		const alreadyHasRemoveColumn = baseColumns.some((col) => col.field === "__category_remove");
		if (alreadyHasRemoveColumn) return baseColumns;
		return [
			...baseColumns,
			{
				field: "__category_remove",
				headerName: t("BUTTON.REMOVE_FROM_CATEGORY"),
				width: 140,
				headerAlign: "center",
				align: "center",
				sortable: false,
				filterable: false,
				renderCell: (params) => (
					<Tooltip title={t("BUTTON.REMOVE_FROM_CATEGORY")}>
						<IconButton
							size="small"
							color="error"
							onClick={(event) => {
								event.stopPropagation();
								onRemoveDocument?.(params.row);
							}}
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				),
			},
		];
	}, [columns, fallbackColumns, onRemoveDocument, t]);

	const handleRowClick = (params) => {
			if (onViewDocument) {
			onViewDocument(params.row);
		}
	};

	const hasDocuments = Array.isArray(documents) && documents.length > 0;

	return (
		<ModalWrapper
			open={open}
			setOpen={() => {}}
			onClose={onClose}
			title={category?.category_name || t("LABELS.CATEGORY")}
			subtitle={t("UI_TEXT.CATEGORY_DOCS_HELP")}
			width="70vw"
			content={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
					{hasDocuments ? (
						<Datagrid
							data={documents}
							columns={datagridColumns}
							rowIdField="id"
							checkboxSelection={false}
							rowClick
							onRowClick={handleRowClick}
							// gridHeight={420}
							// autoHeight={false}
						/>
					) : (
						<Alert severity="info">{t("UI_TEXT.NO_CATEGORY_DOCUMENTS")}</Alert>
					)}
				</Box>
			}
			actions={
				<>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>

						{canDeleteDocuments && !isDefaultCategory && (
							<Button
								size="small"
								variant="outlined"
								color="error"
								startIcon={<DeleteIcon />}
								onClick={() => onDeleteCategory?.(category)}
							>
								{t("BUTTON.DELETE_CATEGORY")}
							</Button>
						)}
					</Box>
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', columnGap: 1.5 }}>
					{canCreateDocuments && (
						<Button
							size="small"
							variant="contained"
							startIcon={<AddCircle fontSize="small" />}
							onClick={() => onAddDocument?.(category)}
						>
							{t("BUTTON.ADD_DOCUMENT")}
						</Button>
					)}
					{canAssignDocuments && (
						<Button
							size="small"
							variant="outlined"
							onClick={() => onAssignDocuments?.(category)}
						>
							{t("BUTTON.ASSIGN")}
						</Button>
					)}
					</Box>
				</>
			}
		/>
	);
};

export default CategoryViewModal;
